// Chrome-less embeddable widget: a single-MLS slice of the Gitcha feed for MLS
// partner iframes. Tenant/MLS gating is server-side by widgetKey.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PropertyListing } from "../../components";
import useWidgetConfig from "../../hooks/useWidgetConfig";
import useWidgetWantAds from "../../hooks/useWidgetWantAds";
import { applyEmbedTheme } from "../../helpers/embedTheme";
import gitchaMark from "../../images/gitcha_h_logo.png";
import bellIcon from "../../images/bell.svg";
import "./IframeWidget.scss";

// The widget has no sort UI — it always shows the most recent want-ads.
const SORT_TYPE = "newest";

const BLS_ROOT_DOMAIN = "thebuyerlistingservice.com";

const tenantOrigin = (slug) => {
  const { protocol, host } = window.location;
  const root = host.includes("localhost")
    ? host.slice(host.indexOf("localhost"))
    : BLS_ROOT_DOMAIN;
  return `${protocol}//${slug}.${root}`;
};

const IframeWidget = () => {
  const { widgetKey } = useParams();

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useWidgetConfig(widgetKey);

  // applyEmbedTheme returns the resolved tenant config (colors + logo paths), so
  // the widget themes AND brands itself from the tenant config - no hardcoding.
  const [tenantTheme, setTenantTheme] = useState(null);
  const [tenantLogoFailed, setTenantLogoFailed] = useState(false);
  useEffect(() => {
    if (config?.tenant_slug)
      applyEmbedTheme(config.tenant_slug).then(setTenantTheme);
  }, [config]);

  const {
    data: feed,
    isLoading: isFeedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useWidgetWantAds({ widgetKey, sortType: SORT_TYPE, enabled: !!config });

  const ads = feed?.ads ?? [];
  const totalCount = feed?.totalCount ?? 0;
  // The brand footer only floats (sticky + gradient scroll-cue) once the list is
  // long enough to actually scroll; with a few records it stays static so the
  // gradient never fades out a card that isn't scrollable.
  const showScrollFade = ads.length > 3;

  // Outbound-only: open the want-ad on the MLS's own environment in a new tab.
  const openAd = (adSlug) => {
    if (adSlug && config?.tenant_slug)
      window.open(
        `${tenantOrigin(config.tenant_slug)}/want-listing-details/${adSlug}`,
        "_blank",
        "noopener",
      );
  };
  const handleItemClick = (_id, slug) => openAd(slug);
  const handleFavorite = (_isFav, wantAdId) =>
    openAd(ads.find((a) => a.Want_ad_Id === wantAdId)?.slug);

  // CTAs → the tenant's BLS environment (its root shows sign-up/sign-in).
  const openTenant = () => {
    if (config?.tenant_slug)
      window.open(tenantOrigin(config.tenant_slug), "_blank", "noopener");
  };

  if (isConfigLoading) {
    return (
      <div className="gitcha-embed-widget">
        <div className="gew-loading">Loading…</div>
      </div>
    );
  }

  if (isConfigError || !config) {
    return (
      <div className="gitcha-embed-widget">
        <div className="gew-state">This widget is unavailable.</div>
      </div>
    );
  }

  const mls = (config.tenant_slug || "").toUpperCase();
  const isBlc = (config.tenant_slug || "").toLowerCase() === "blc";
  // Tenant wordmark from the tenant config. Prefer a light-header logo
  // (logoOnLight) when a tenant's primary logo is white/unsuited to the light
  // header; fall back to logoV1, then the Gitcha mark so the top is never blank.
  const tenantLogos = tenantTheme?.assets?.logos || {};
  const tenantLogo = tenantLogos.logoOnLight || tenantLogos.logoV1;
  const topLogoSrc =
    tenantLogo && !tenantLogoFailed ? tenantLogo : gitchaMark;

  return (
    <div className={`gitcha-embed-widget${isBlc ? " gew-blc" : ""}`}>
      {isBlc && (
        <div className="gew-bellbar">
          <button
            type="button"
            className="gew-bell"
            onClick={openTenant}
            aria-label="Notifications"
          >
            <img src={bellIcon} alt="" />
            <span className="gew-bell-dot" />
          </button>
        </div>
      )}
      {!isBlc && (
        <div className="gew-stickytitle">Active Buyer Hot Sheet</div>
      )}
      {!isBlc && (
      <div className="gew-header">
        <div className="gew-topbar">
          <img
            src={topLogoSrc}
            alt={`${mls} Active Buyers`}
            className="gew-logo"
            onError={() => setTenantLogoFailed(true)}
          />
          <div className="gew-actions">
            <button
              type="button"
              className="gew-cta gew-cta-secondary"
              onClick={openTenant}
            >
              View Matches
            </button>
            <button
              type="button"
              className="gew-cta gew-cta-primary"
              onClick={openTenant}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M17.4999 2.50006C17.3455 2.34484 17.1485 2.23915 16.9339 2.19649C16.7193 2.15383 16.4966 2.17614 16.2949 2.26056L2.89494 7.78556C2.6815 7.87613 2.50189 8.03214 2.38213 8.23114C2.26237 8.43015 2.20862 8.66199 2.22837 8.89375C2.24812 9.12551 2.3404 9.34538 2.49256 9.52252C2.64473 9.69966 2.84925 9.82536 3.07619 9.88131L8.68119 11.2826L10.0862 16.8876C10.1402 17.1119 10.2621 17.3147 10.4349 17.4673C10.6078 17.6199 10.8236 17.7148 11.0524 17.7388C11.0762 17.7413 11.1012 17.7426 11.1262 17.7426C11.3339 17.7432 11.5375 17.6857 11.7131 17.5772C11.8887 17.4688 12.029 17.3138 12.1187 17.1288L17.7437 5.70131C17.8257 5.50089 17.8466 5.28019 17.8037 5.06752C17.7608 4.85485 17.6561 4.65958 17.5024 4.50631L17.4999 2.50006ZM11.1262 16.4926L9.80869 11.2051L12.5074 8.50631C12.6247 8.38903 12.6906 8.23001 12.6906 8.06418C12.6906 7.89836 12.6247 7.73934 12.5074 7.62206C12.3902 7.50478 12.2311 7.43889 12.0653 7.43889C11.8995 7.43889 11.7405 7.50478 11.6232 7.62206L8.92244 10.3221L3.62994 8.87506L16.2562 3.69381L11.1262 16.4926Z"
                  fill="currentColor"
                />
              </svg>
              Post Want-Listing
            </button>
          </div>
        </div>
        <div className="gew-titles">
          <div className="gew-title">Recently Added Buyers</div>
        </div>
      </div>
      )}

      <div className="gew-list">
        {ads.length === 0 && !isFeedLoading ? (
          <div className="gew-state">No buyer want-listings right now.</div>
        ) : (
          ads.map((ad) => (
            <PropertyListing
              key={ad.Want_ad_Id}
              list={ad}
              embedMode
              embedShareBaseUrl={tenantOrigin(config.tenant_slug)}
              handleWantAdItemClick={handleItemClick}
              handleFavoriteOrUnfavoriteWantAd={handleFavorite}
              onChangeText={() => { }}
              onAddClient={() => { }}
            />
          ))
        )}
        {(isFeedLoading || isFetchingNextPage) && (
          <div className="gew-loading">Loading…</div>
        )}
      </div>

      {hasNextPage && (
        <div className="gew-footer">
          <button
            type="button"
            className="gew-showmore"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            View all {totalCount} buyers
          </button>
        </div>
      )}

      {!isBlc && (
        <div
          className={`gew-brandfooter${
            showScrollFade ? " gew-brandfooter-floating" : ""
          }`}
        >
          <img src={gitchaMark} alt="" className="gew-footer-mark" />
          <span>The Buyer Listing Service&reg;</span>
        </div>
      )}
    </div>
  );
};

export default IframeWidget;
